import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDateTime } from "@/lib/apiClient";
import { roleLabel } from "@/lib/role";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Users, Building2, MapPin, ConciergeBell, ArrowRight } from "lucide-react";

export default function SupportDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/support/dashboard").then((r) => setD(r.data)); }, []);
  return (
    <AdminLayout nav={amdenNav} title="Dashboard">
      <PageHeader title="Amden Super Admin" subtitle="Platform health across every client company" />
      {!d ? <div className="text-zinc-400">Loading…</div> : (
        <div className="space-y-6">
          {/* Hero banner — platform totals only, no client financials */}
          <div className="relative overflow-hidden rounded-xl bg-[#0F172A] text-white p-6 md:p-8">
            <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-amber-500/10" />
            <div className="absolute right-32 bottom-0 h-40 w-40 rounded-full bg-sky-500/10" />
            <div className="relative z-10">
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-6">Platform Overview · Live</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                <HeroStat testId="hero-companies" icon={Building2} label="Active Companies" value={`${d.active_companies}`} sub={`${d.total_companies} total`} />
                <HeroStat testId="hero-users" icon={Users} label="Active Users" value={`${d.active_users}`} sub={`${d.total_users} total`} />
                <HeroStat testId="hero-sites" icon={MapPin} label="Active Sites" value={`${d.active_sites}`} sub={`${d.total_employees} employees`} />
              </div>
            </div>
          </div>

          {/* Requested Services tile + quick actions */}
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/amden/service-requests" data-testid="requested-services-tile" className="group bg-white border border-zinc-200 rounded-xl p-5 hover:border-amber-400 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg bg-amber-500/10 flex items-center justify-center"><ConciergeBell className="h-6 w-6 text-amber-600" /></div>
                <span className="num text-3xl font-black text-slate-900" data-testid="pending-requests-count">{d.pending_service_requests}</span>
              </div>
              <div className="mt-4 font-heading font-bold text-slate-900">Service Quote Requests</div>
              <div className="text-sm text-zinc-500 flex items-center gap-1 mt-1">Review & send quotes <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></div>
            </Link>
            <Link to="/amden/companies" className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-slate-400 hover:shadow-md transition-all">
              <div className="h-11 w-11 rounded-lg bg-slate-900/5 flex items-center justify-center"><Building2 className="h-6 w-6 text-slate-700" /></div>
              <div className="mt-4 font-heading font-bold text-slate-900">Manage Companies</div>
              <div className="text-sm text-zinc-500 mt-1">Onboard, brand & configure clients</div>
            </Link>
            <Link to="/amden/amden-users" className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-slate-400 hover:shadow-md transition-all">
              <div className="h-11 w-11 rounded-lg bg-slate-900/5 flex items-center justify-center"><Users className="h-6 w-6 text-slate-700" /></div>
              <div className="mt-4 font-heading font-bold text-slate-900">Amden Users</div>
              <div className="text-sm text-zinc-500 mt-1">Manage super-admin accounts</div>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-zinc-200 rounded-md">
              <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-wider text-zinc-500">Recent Logins</div>
              <div className="divide-y divide-zinc-50">
                {d.recent_logins.map((u) => (
                  <div key={u.id} className="px-4 py-2.5 flex justify-between text-sm"><span>{u.name} <span className="text-xs text-zinc-400">({roleLabel(u.role)})</span></span><span className="num text-xs text-zinc-500">{fmtDateTime(u.last_login)}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md">
              <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-wider text-zinc-500">Recent Config Changes</div>
              <div className="divide-y divide-zinc-50">
                {d.config_changes.length === 0 && <div className="px-4 py-3 text-sm text-zinc-400">None</div>}
                {d.config_changes.map((c) => (
                  <div key={c.id} className="px-4 py-2.5 flex justify-between text-sm"><span>{c.action} <span className="text-xs text-zinc-400">by {c.user_name}</span></span><span className="num text-xs text-zinc-500">{fmtDateTime(c.timestamp)}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function HeroStat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId}>
      <Icon className="h-6 w-6 text-amber-500 mb-3" />
      <div className="num text-4xl md:text-5xl font-black leading-none">{value}</div>
      <div className="text-sm text-zinc-300 mt-2">{label}</div>
      <div className="text-xs text-zinc-500">{sub}</div>
    </div>
  );
}
